/**
 * OnboardingTour - Product tour using Driver.js
 *
 * Features:
 * - Admin/Agent/Supervisor tour tracks (role-scoped — each role only sees pages they can access)
 * - Integration with tooltip system via data-help-id
 * - Persistent completion status in localStorage
 * - i18n support for step content
 *
 * DOM targets used:
 *   data-tour="kpi-grid"              – KPI cards row (all dashboards)
 *   data-tour="agent-coaching"        – AI Coaching + Supervisor Notes section
 *   data-tour="supervisor-leaderboard"– Team Overview card
 *   data-tour="sidebar-search"        – Command palette trigger (⌘K)
 *   data-testid="sidebar-nav-{id}"    – Sidebar nav items (always present)
 *   data-testid="sidebar"             – Sidebar container
 *
 * Usage:
 *   <OnboardingTour tour="admin" />
 */

import { useEffect, useCallback, useRef } from 'react';
import type { DriveStep, Driver } from 'driver.js';
import './driver-theme.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';

type TourType = 'admin' | 'agent' | 'supervisor';

interface OnboardingTourProps {
  /**
   * Which tour to run
   */
  tour: TourType;

  /**
   * Force start the tour (override localStorage)
   */
  forceStart?: boolean;

  /**
   * The screen the tour points at can no longer be swapped for another one.
   * The tour is marked seen the moment it starts, so while the dashboard on
   * screen may still change (the feature flags have not answered and the
   * unified preview may replace it), nothing starts and nothing is consumed —
   * not the first-visit auto-start, not a queued request. Default: settled.
   */
  screenSettled?: boolean;

  /**
   * Callback when tour completes
   */
  onComplete?: () => void;
}

const STORAGE_KEY = 'aica-onboarding-completed';
const SEEN_KEY = 'aica-onboarding-seen';
const PENDING_KEY = 'aica-tour-pending';

/** Tours that have been auto-started at least once — won't auto-start again */
function getSeenTours(): TourType[] {
  try {
    const stored = localStorage.getItem(SEEN_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function markTourSeen(tour: TourType): void {
  try {
    const seen = getSeenTours();
    if (!seen.includes(tour)) {
      seen.push(tour);
      localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    }
  } catch {
    // ignore
  }
}

/**
 * Get completed tours from localStorage
 */
function getCompletedTours(): TourType[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Mark tour as completed
 */
function markTourComplete(tour: TourType): void {
  try {
    const completed = getCompletedTours();
    if (!completed.includes(tour)) {
      completed.push(tour);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
    }
  } catch {
    // Silently fail if localStorage unavailable
  }
}

export function OnboardingTour({
  tour,
  forceStart = false,
  screenSettled = true,
  onComplete,
}: OnboardingTourProps) {
  const { t } = useTranslation(['onboarding', 'settings', 'common']);
  const driverRef = useRef<Driver | null>(null);

  // Define tour steps based on tour type.
  // Each role's tour only references pages and features accessible to that role.
  const getSteps = useCallback((): DriveStep[] => {
    switch (tour) {
      // ─── ADMIN ──────────────────────────────────────────────────────────────
      // Full platform access: KPIs, Records, Analyses, Projects, Alerts, Settings
      case 'admin':
        return [
          {
            // Floating step — no element needed
            popover: {
              title: t('onboarding.admin.step1.title', 'Ласкаво просимо до Aelo! 👋'),
              description: t(
                'onboarding.admin.step1.content',
                'Aelo — AI-платформа для аналізу комунікацій. Автоматично транскрибує розмови, оцінює якість та дає рекомендації. Давайте разом пройдемось по головних можливостях.'
              ),
              align: 'center',
            },
          },
          {
            element: '[data-tour="kpi-grid"]',
            popover: {
              title: t('onboarding.admin.step2.title', 'Ключові показники бізнесу'),
              description: t(
                'onboarding.admin.step2.content',
                "П'ять KPI в реальному часі: середній бал якості, кількість аналізів, задоволеність клієнтів, ймовірність продажу та вартість аналізу. Кожна картка є інтерактивною — натисніть, щоб перейти до деталей."
              ),
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '[data-testid="sidebar-nav-records"]',
            popover: {
              title: t('onboarding.admin.step3.title', 'Комунікації'),
              description: t(
                'onboarding.admin.step3.content',
                'Дзвінки та чати вашої команди. Завантажуйте аудіо вручну або налаштуйте автоматичну інтеграцію з CRM. Кожна розмова аналізується AI: транскрипція, оцінка якості, сильні та слабкі сторони агента.'
              ),
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-testid="sidebar-nav-records"]',
            popover: {
              title: t('onboarding.admin.step4.title', 'Оцінки якості в таблиці'),
              description: t(
                'onboarding.admin.step4.content',
                'У таблиці записів — колонки якості: ймовірність продажу, тональність та оцінки за категоріями (вітання, виявлення потреб, робота із запереченнями, закриття). Приховані колонки вмикаються перемикачем, є фільтри за QA-діапазоном і тональністю, а клік по рядку відкриває повний AI-звіт.'
              ),
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-testid="sidebar-nav-projects"]',
            popover: {
              title: t('onboarding.admin.step5.title', 'Проекти'),
              description: t(
                'onboarding.admin.step5.content',
                'Організуйте записи по командах, кампаніях або продуктах. Кожен проект має власні критерії оцінки, підключені канали та склад агентів.'
              ),
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-testid="sidebar-nav-alerts"]',
            popover: {
              title: t('onboarding.admin.step6.title', 'Сигнали якості'),
              description: t(
                'onboarding.admin.step6.content',
                'Автоматичні сигнали, коли агент падає нижче порогу якості. Налаштуйте правила: мінімальний бал, частота порушень, кому надсилати — і більше не пропустіть жодної проблеми.'
              ),
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-tour="sidebar-search"]',
            popover: {
              title: t('onboarding.admin.step7.title', 'Швидкий пошук ⌘K'),
              description: t(
                'onboarding.admin.step7.content',
                'Натисніть ⌘K (або Ctrl+K) — знайдіть будь-який запис, агента, проект або налаштування за секунди. Підтримує фільтрацію по даті, оцінці та статусу.'
              ),
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-testid="sidebar-nav-settings"]',
            popover: {
              title: t('onboarding.admin.step8.title', 'Налаштування платформи'),
              description: t(
                'onboarding.admin.step8.content',
                'Завершіть налаштування: встановіть бюджет, налаштуйте критерії якості та запросіть команду. Розпочніть звідси — це перший крок до роботи з реальними даними.'
              ),
              side: 'right',
              align: 'start',
            },
          },
        ];

      // ─── AGENT ──────────────────────────────────────────────────────────────
      // Personal performance focus. No access to: Review Queue, full Settings,
      // Projects management, Alerts configuration (agents see alerts about them,
      // not configure them).
      case 'agent':
        return [
          {
            popover: {
              title: t('onboarding.agent.step1.title', 'Ваш особистий дашборд 🎯'),
              description: t(
                'onboarding.agent.step1.content',
                'Ласкаво просимо! Тут відображається ваша особиста статистика: бал якості, кількість дзвінків, динаміка та рейтинг у команді. Все що вам потрібно для зростання — в одному місці.'
              ),
              align: 'center',
            },
          },
          {
            element: '[data-tour="kpi-grid"]',
            popover: {
              title: t('onboarding.agent.step2.title', 'Ваші показники'),
              description: t(
                'onboarding.agent.step2.content',
                'Ваш бал якості, дзвінки за період, тренд якості, місце в рейтингу команди та коучинг. Дані оновлюються після кожного нового аналізу.'
              ),
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '[data-tour="agent-coaching"]',
            popover: {
              title: t('onboarding.agent.step3.title', 'AI-коучинг'),
              description: t(
                'onboarding.agent.step3.content',
                'Персональні рекомендації від AI на основі ваших останніх дзвінків: тижневий план покращення, нотатки від супервайзера та конкретні поради по слабких зонах.'
              ),
              side: 'top',
              align: 'start',
            },
          },
          {
            element: '[data-testid="sidebar-nav-records"]',
            popover: {
              title: t('onboarding.agent.step4.title', 'Мої комунікації'),
              description: t(
                'onboarding.agent.step4.content',
                'Всі ваші дзвінки та чати з результатами AI-аналізу. Перегляньте транскрипцію, прослухайте аудіо, побачте де саме знизилась оцінка — і розумійте як покращитись.'
              ),
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-testid="sidebar-nav-records"]',
            popover: {
              title: t('onboarding.agent.step5.title', 'Ваші оцінки — одразу в списку'),
              description: t(
                'onboarding.agent.step5.content',
                'Оцінки якості видно прямо в списку: тональність і бали за категоріями для кожного дзвінка. Клікніть по дзвінку, щоб побачити повну розбивку з цитатами з розмови — і зрозуміти, де саме зростати.'
              ),
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-tour="sidebar-search"]',
            popover: {
              title: t('onboarding.agent.step6.title', 'Швидкий пошук ⌘K'),
              description: t(
                'onboarding.agent.step6.content',
                'Знайдіть будь-який свій дзвінок за датою, клієнтом або оцінкою. Натисніть ⌘K або клікніть тут.'
              ),
              side: 'right',
              align: 'start',
            },
          },
        ];

      // ─── SUPERVISOR ─────────────────────────────────────────────────────────
      // Team management focus. Has access to: Review Queue, Alerts (view + configure),
      // team analyses, leaderboard. No access to: billing, AI provider settings,
      // full platform configuration.
      case 'supervisor':
        return [
          {
            popover: {
              title: t(
                'onboarding.supervisor.step1.title',
                'Центр управління командою 👥'
              ),
              description: t(
                'onboarding.supervisor.step1.content',
                'Ласкаво просимо! Відстежуйте ефективність вашої команди, виявляйте агентів, які потребують уваги, та керуйте якістю на основі даних — не інтуїції.'
              ),
              align: 'center',
            },
          },
          {
            element: '[data-tour="kpi-grid"]',
            popover: {
              title: t('onboarding.supervisor.step2.title', 'Показники команди'),
              description: t(
                'onboarding.supervisor.step2.content',
                'Оцінка команди, оброблені дзвінки та кількість агентів, які потребують уваги. Всі показники з трендами за обраний період.'
              ),
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '[data-tour="supervisor-leaderboard"]',
            popover: {
              title: t('onboarding.supervisor.step3.title', 'Огляд команди'),
              description: t(
                'onboarding.supervisor.step3.content',
                'Рейтинг агентів по балу якості, тренди кожного. Одразу видно хто зростає, а хто потребує коучингу. Натисніть на агента, щоб перейти до його особистих показників.'
              ),
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '[data-testid="sidebar-nav-review-queue"]',
            popover: {
              title: t('onboarding.supervisor.step4.title', 'Черга перевірки'),
              description: t(
                'onboarding.supervisor.step4.content',
                'Дзвінки, позначені для ручної перевірки: низька оцінка AI, скарга клієнта або випадкова вибірка. Переглядайте, залишайте нотатки агентам та закривайте задачі.'
              ),
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-testid="sidebar-nav-alerts"]',
            popover: {
              title: t('onboarding.supervisor.step5.title', 'Сигнали якості'),
              description: t(
                'onboarding.supervisor.step5.content',
                'Автоматичні сигнали про порушення порогів якості. Налаштуйте правила один раз — і Aelo сама попередить, коли агент потребує уваги, без щоденного моніторингу вручну.'
              ),
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-testid="sidebar-nav-records"]',
            popover: {
              title: t(
                'onboarding.supervisor.step6.title',
                'Якість команди в одному списку'
              ),
              description: t(
                'onboarding.supervisor.step6.content',
                'Оцінки якості всіх дзвінків команди — прямо в таблиці записів. Фільтруйте за агентом, проєктом, QA-діапазоном і тональністю — і дивіться, де саме падає якість: у якій категорії та в кого.'
              ),
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-tour="sidebar-search"]',
            popover: {
              title: t('onboarding.supervisor.step7.title', 'Пошук по всій команді ⌘K'),
              description: t(
                'onboarding.supervisor.step7.content',
                'Знайдіть будь-який дзвінок, агента чи проект миттєво. Використовуйте ⌘K для швидкого доступу без навігації по меню.'
              ),
              side: 'right',
              align: 'start',
            },
          },
        ];

      default:
        return [];
    }
  }, [tour, t]);

  // Dynamic import in `launch` — keeps driver.js (+ its CSS) out of the main
  // bundle. The tour is shown once per role, so most users never pay the
  // download cost.
  // Which life of the component a launch belongs to: cleanup moves it on, and a
  // launch still loading `driver.js` finds its own gone. `launchingRef` holds
  // the life that has a launch on its way: a second request in the same life
  // does not open a second tour, the next life's is not blocked by a stale one.
  const epochRef = useRef(0);
  const launchingRef = useRef<number | null>(null);
  const launch = useCallback(async () => {
    const epoch = epochRef.current;
    if (launchingRef.current === epoch || driverRef.current?.isActive()) return;
    const steps = getSteps().filter((step) => {
      if (!step.element) return true;
      return !!document.querySelector(step.element as string);
    });
    if (steps.length === 0) return;

    launchingRef.current = epoch;
    let driverModule: typeof import('driver.js');
    try {
      [driverModule] = await Promise.all([
        import('driver.js'),
        import('driver.js/dist/driver.css'),
      ]);
    } finally {
      if (launchingRef.current === epoch) launchingRef.current = null;
    }
    if (epoch !== epochRef.current) return;
    const { driver } = driverModule;

    const driverObj = driver({
      steps,
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      popoverClass: 'aica-driver-popover',
      progressText: t('common:common.progress', '{{current}} з {{total}}'),
      nextBtnText: t('common:common.next', 'Далі →'),
      prevBtnText: t('common:common.back', '← Назад'),
      doneBtnText: t('common:common.finish', 'Завершити ✓'),
      smoothScroll: true,
      allowClose: true,
      overlayOpacity: document.documentElement.classList.contains('dark') ? 0.55 : 0.4,
      stagePadding: 8,
      stageRadius: 8,
      onDestroyed: (_element, _step, options) => {
        const currentStepIndex = options.state?.activeIndex ?? 0;
        const isCompleted = currentStepIndex >= steps.length - 1;
        if (isCompleted) {
          markTourComplete(tour);
          onComplete?.();
        }
      },
    });

    markTourSeen(tour);
    driverRef.current = driverObj;
    driverObj.drive();
  }, [getSteps, t, tour, onComplete]);

  // An explicit request (the help menu, the checklist) starts the tour at once:
  // whoever asks is looking at the screen. Not gated by `screenSettled`, and not
  // restarted by it — the auto-start below is.
  useEffect(() => {
    function handleForce(e: Event) {
      if ((e as CustomEvent).detail === tour) void launch();
    }
    window.addEventListener('aica-tour-start', handleForce);
    return () => {
      epochRef.current += 1;
      window.removeEventListener('aica-tour-start', handleForce);
      driverRef.current?.destroy();
    };
  }, [tour, launch]);

  useEffect(() => {
    if (!screenSettled) return;
    const pending = localStorage.getItem(PENDING_KEY);
    const hasPending = pending === tour;
    if (hasPending) localStorage.removeItem(PENDING_KEY);

    const completed = getCompletedTours();
    const seen = getSeenTours();
    const shouldRun =
      forceStart || hasPending || (!completed.includes(tour) && !seen.includes(tour));

    if (shouldRun) {
      void launch();
    }
  }, [tour, forceStart, screenSettled, launch]);

  // Driver.js doesn't render a React component
  return null;
}

/**
 * Drop a tour request that is waiting for `/dashboard`. The unified shell has
 * no tour, so a request that reaches it would stay in the queue and start by
 * itself on the day the preview is switched off.
 */
export function clearPendingTour(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // Silently fail if localStorage unavailable
  }
}

/**
 * Reset tour completion status (for testing or user request)
 */
export function resetTours(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Hook to control tour programmatically
 */
export function useOnboardingTour(tour: TourType) {
  const navigate = useNavigate();

  const startTour = useCallback(() => {
    // Remove tour from completed and seen lists (manual start always allowed)
    const completed = getCompletedTours();
    const filtered = completed.filter((t) => t !== tour);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));

    const seen = getSeenTours();
    const filteredSeen = seen.filter((t) => t !== tour);
    localStorage.setItem(SEEN_KEY, JSON.stringify(filteredSeen));

    if (window.location.pathname === '/dashboard') {
      // Already on dashboard — dispatch event to trigger OnboardingTour directly
      window.dispatchEvent(new CustomEvent('aica-tour-start', { detail: tour }));
    } else {
      // Set pending flag, then SPA navigate — instant, no full page reload
      localStorage.setItem(PENDING_KEY, tour);
      void navigate({ to: '/dashboard' });
    }
  }, [tour, navigate]);

  const completeTour = useCallback(() => {
    markTourComplete(tour);
  }, [tour]);

  const shouldShow = useCallback(() => {
    const completed = getCompletedTours();
    return !completed.includes(tour);
  }, [tour]);

  return {
    shouldShow: shouldShow(),
    startTour,
    completeTour,
  };
}

export default OnboardingTour;
